/**
 * Floating toolbar shown above a selected node. Mirrors the look of the
 * TipTap BubbleMenu (rounded border + shadow, small icon buttons in a row).
 * Positioning is absolute relative to the node frame.
 */

import type { ReactNode } from 'react';

export interface ToolbarButton {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
}

interface NodeToolbarProps {
  visible: boolean;
  buttons: ToolbarButton[];
  /** Optional inline content (e.g. span slider for columns). */
  extra?: ReactNode;
}

export function NodeToolbar({ visible, buttons, extra }: NodeToolbarProps) {
  if (!visible) return null;
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="absolute -top-9 right-0 z-30 flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-1 shadow-md dark:border-slate-700 dark:bg-slate-900"
    >
      {extra}
      {buttons.map((b) => (
        <button
          key={b.key}
          type="button"
          disabled={b.disabled}
          onClick={(e) => {
            e.stopPropagation();
            b.onClick();
          }}
          title={b.label}
          className={`inline-flex h-7 items-center gap-1 rounded px-1.5 text-xs transition ${
            b.variant === 'destructive'
              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40'
              : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
          } ${b.disabled ? 'cursor-not-allowed opacity-30' : ''}`}
        >
          {b.icon}
          <span className="hidden sm:inline">{b.label}</span>
        </button>
      ))}
    </div>
  );
}
